import { empty, ofList } from "./.fable/fable-library.3.1.16/Set.js";
import { getModJack, VcfState, GainState, Mod, ScopeState, Message, OscState, IOState, ModState, LfoState, ModDef, Jack, Control, ControlConfig, KnobConfig } from "./Model.js";
import { map as map_1, singleton, ofArray } from "./.fable/fable-library.3.1.16/List.js";
import { comparePrimitives, uncurry, compare } from "./.fable/fable-library.3.1.16/Util.js";
import { map as map_3, change as change_1, tryFind, ofList as ofList_1 } from "./.fable/fable-library.3.1.16/Map.js";
import { map2, bind, map as map_2, some } from "./.fable/fable-library.3.1.16/Option.js";
import { Union } from "./.fable/fable-library.3.1.16/Types.js";
import { union_type, tuple_type, option_type, int32_type, class_type } from "./.fable/fable-library.3.1.16/Reflection.js";
import { printf, toConsole } from "./.fable/fable-library.3.1.16/String.js";
import { equalsWith } from "./.fable/fable-library.3.1.16/Array.js";

const osc = new ModDef("osc", [1, 2], ofList(ofArray([new Control("freq", new ControlConfig(0, new KnobConfig(0, 10, 1000))), new Control("detune", new ControlConfig(0, new KnobConfig(-1, 1, 1000)))]), {
    Compare: (x, y) => compare(x, y),
}), ofList(ofArray([new Jack(true, "sqr"), new Jack(true, "sine"), new Jack(true, "saw"), new Jack(true, "tri"), new Jack(false, "detune"), new Jack(false, "freq")]), {
    Compare: (x_1, y_1) => compare(x_1, y_1),
}));

const io = new ModDef("I/O", [1, 1], empty({
    Compare: (x, y) => compare(x, y),
}), ofList(ofArray([new Jack(true, "MIDI"), new Jack(false, "l out"), new Jack(false, "r out")]), {
    Compare: (x_1, y_1) => compare(x_1, y_1),
}));

const lfo = new ModDef("lfo", [1, 2], ofList(ofArray([new Control("offset", new ControlConfig(0, new KnobConfig(-5, 5, 1000))), new Control("rate", new ControlConfig(0, new KnobConfig(1 / 60, 100, 1000)))]), {
    Compare: (x, y) => compare(x, y),
}), ofList(ofArray([new Jack(false, "rate"), new Jack(true, "sqr"), new Jack(true, "sine"), new Jack(true, "saw"), new Jack(true, "tri")]), {
    Compare: (x_1, y_1) => compare(x_1, y_1),
}));

const gain = new ModDef("gain", [1, 1], ofList(singleton(new Control("gain", new ControlConfig(0, new KnobConfig(0, 3, 1000)))), {
    Compare: (x, y) => compare(x, y),
}), ofList(ofArray([new Jack(true, "out"), new Jack(false, "in"), new Jack(false, "gain")]), {
    Compare: (x_1, y_1) => compare(x_1, y_1),
}));

const scope = new ModDef("scope", [3, 3], ofList(ofArray([new Control("duration", new ControlConfig(0, new KnobConfig(0.05, 10, 199))), new Control("range", new ControlConfig(0, new KnobConfig(0.05, 10, 199)))]), {
    Compare: (x, y) => compare(x, y),
}), ofList(singleton(new Jack(false, "in")), {
    Compare: (x_1, y_1) => compare(x_1, y_1),
}));

const vcf = new ModDef("vcf", [1, 2], ofList(ofArray([new Control("freq", new ControlConfig(0, new KnobConfig(0.05, 22000, 1000))), new Control("q", new ControlConfig(0, new KnobConfig(0.05, 10, 199))), new Control("gain", new ControlConfig(0, new KnobConfig(0.05, 10, 199)))]), {
    Compare: (x, y) => compare(x, y),
}), ofList(ofArray([new Jack(false, "freq"), new Jack(false, "in"), new Jack(true, "out")]), {
    Compare: (x_1, y_1) => compare(x_1, y_1),
}));

export const defs = ofList_1(map_1((_arg1) => {
    const m = _arg1;
    return [m.name, m];
}, ofArray([osc, lfo, io, gain, scope, vcf])));

function buildOscillator(context, oscType) {
    const osc_1 = context.createOscillator();
    osc_1.type = oscType;
    osc_1.start();
    return osc_1;
}

function DCOffset_param(name, node) {
    return (node.parameters["get"])(name);
}

export const DCOffset_amountParam = (node) => DCOffset_param("amount", node);

export function DCOffset_create(context) {
    return new AudioWorkletNode(context, "dc-offset", some({
        amount: 0,
    }));
}

export function DCOffset_amount(node) {
    return DCOffset_amountParam(node).value;
}

export function DCOffset_setAmount(amount, node) {
    DCOffset_param("amount", node).value = amount;
    return node;
}

function initLfo(context) {
    const freq = context.createConstantSource();
    const offset = context.createConstantSource();
    const nodes = ofList_1(map_1((oscType) => {
        const osc_1 = buildOscillator(context, oscType);
        const dcOffset = DCOffset_create(context);
        offset.connect(DCOffset_amountParam(dcOffset));
        osc_1.frequency.value = (1 / 2400);
        freq.connect(osc_1.frequency);
        void osc_1.connect(dcOffset);
        return [oscType, dcOffset];
    }, ofArray(["sawtooth", "sine", "square", "triangle"])));
    freq.start();
    offset.start();
    return new ModState(1, new LfoState(nodes, offset.offset, freq.offset));
}

function initOut(context) {
    const merger = context.createChannelMerger(2);
    void merger.connect(context.destination);
    const midiPitch = context.createConstantSource();
    midiPitch.start();
    return new ModState(3, new IOState(merger, midiPitch));
}

function initOsc(context) {
    const voltageToFrequency = context.createGain();
    voltageToFrequency.gain.value = 110;
    const freq = context.createConstantSource();
    void freq.connect(voltageToFrequency);
    const voltageToDetune = context.createGain();
    voltageToDetune.gain.value = 100;
    const detune = context.createConstantSource();
    detune.offset.value = 0;
    void detune.connect(voltageToDetune);
    const nodes = ofList_1(map_1((t) => {
        const osc_1 = buildOscillator(context, t);
        osc_1.frequency.value = 0;
        osc_1.detune.value = 0;
        voltageToDetune.connect(osc_1.detune);
        voltageToFrequency.connect(osc_1.frequency);
        return [t, osc_1];
    }, ofArray(["sawtooth", "sine", "square", "triangle"])));
    freq.start();
    detune.start();
    return new ModState(0, new OscState(nodes, detune.offset, freq.offset));
}

export function initScope(context, position_0, position_1, dispatch) {
    const analyser = new AudioWorkletNode(context, "scope");
    analyser.port.onmessage = ((e) => dispatch(new Message(16, [position_0, position_1], new Float32Array(e.data))));
    return new Mod(scope, new ModState(2, new ScopeState(analyser, 0.1, 0, 1, void 0, new Float32Array([]))));
}

function initGain(context) {
    const gain_1 = context.createGain();
    return new ModState(4, new GainState(gain_1, gain_1, gain_1.gain));
}

function initVcf(context) {
    const bqf = context.createBiquadFilter();
    const freqGain = context.createGain();
    freqGain.gain.value = 11000;
    freqGain.connect(bqf.frequency);
    return new ModState(5, new VcfState(bqf, freqGain, bqf));
}

function init(context, def) {
    let matchValue;
    return map_2((state) => (new Mod(def, state)), (matchValue = def.name, (matchValue === "I/O") ? initOut(context) : ((matchValue === "osc") ? initOsc(context) : ((matchValue === "lfo") ? initLfo(context) : ((matchValue === "gain") ? initGain(context) : ((matchValue === "vcf") ? initVcf(context) : (void 0)))))));
}

export function build(name, context) {
    return bind((def) => init(context, def), tryFind(name, defs));
}

export class DestConnectArgs extends Union {
    constructor(tag, ...fields) {
        super();
        this.tag = (tag | 0);
        this.fields = fields;
    }
    cases() {
        return ["AudioNode", "AudioParam"];
    }
}

export function DestConnectArgs$reflection() {
    return union_type("Patchy.Client.Mod.DestConnectArgs", [], DestConnectArgs, () => [[["Item", tuple_type(class_type("Fable.WebAudio.AudioNode"), option_type(int32_type))]], [["Item", class_type("Fable.WebAudio.AudioParam")]]]);
}

function connectNodes(_arg1_0, _arg1_1) {
    const _arg1 = [_arg1_0, _arg1_1];
    const srcNode = _arg1[0];
    const output = _arg1[1];
    return (_arg2) => {
        if (_arg2.tag === 1) {
            srcNode.connect(_arg2.fields[0], output);
        }
        else {
            void srcNode.connect(_arg2.fields[0][0], output, _arg2.fields[0][1]);
        }
    };
}

function disconnectNodes(_arg1_0, _arg1_1) {
    const _arg1 = [_arg1_0, _arg1_1];
    const srcNode = _arg1[0];
    const output = _arg1[1];
    return (_arg2) => {
        if (_arg2.tag === 1) {
            const param = _arg2.fields[0];
            if (output == null) {
                srcNode.disconnect(param);
            }
            else {
                srcNode.disconnect(param, output);
            }
        }
        else {
            const node = _arg2.fields[0][0];
            const matchValue = [output, _arg2.fields[0][1]];
            if (matchValue[0] != null) {
                if (matchValue[1] != null) {
                    const inputNumber = matchValue[1] | 0;
                    const outputNumber = matchValue[0] | 0;
                    srcNode.disconnect(node, outputNumber, inputNumber);
                }
                else {
                    const outputNumber_1 = matchValue[0] | 0;
                    srcNode.disconnect(node, outputNumber_1);
                }
            }
            else {
                srcNode.disconnect(node);
            }
        }
    };
}

function labelToOscType(_arg1) {
    switch (_arg1) {
        case "sqr": {
            return "square";
        }
        case "sine": {
            return "sine";
        }
        case "saw": {
            return "sawtooth";
        }
        case "tri": {
            return "triangle";
        }
        default: {
            return void 0;
        }
    }
}

function toAudioSource(jackNumber, md) {
    const matchValue = md.state;
    switch (matchValue.tag) {
        case 0: {
            return map_2((node) => [node, void 0], bind((_arg1) => bind((func) => func(matchValue.fields[0].nodes), map_2((key) => ((table) => tryFind(key, table)), labelToOscType(_arg1.label))), getModJack(jackNumber, md)));
        }
        case 4: {
            return [matchValue.fields[0].o, void 0];
        }
        case 5: {
            return [matchValue.fields[0].o, void 0];
        }
        case 3: {
            return [matchValue.fields[0].midiPitch, void 0];
        }
        case 1: {
            return map_2((node_2) => [node_2, void 0], bind((_arg1_2) => bind((func_1) => func_1(matchValue.fields[0].nodes), map_2((key_1) => ((table_1) => tryFind(key_1, table_1)), labelToOscType(_arg1_2.label))), getModJack(jackNumber, md)));
        }
        default: {
            return void 0;
        }
    }
}

function toAudioDest(jackNumber, md) {
    let clo1, merger;
    const matchValue = [md.state, jackNumber];
    let pattern_matching_result, merger_1, freq, detune, value, input, node, rate, freq_1, input_1;
    if (matchValue[0].tag === 0) {
        if (matchValue[1] === 0) {
            pattern_matching_result = 2;
            detune = matchValue[0].fields[0].detune;
        }
        else if (matchValue[1] === 1) {
            pattern_matching_result = 1;
            freq = matchValue[0].fields[0].freq;
        }
        else {
            pattern_matching_result = 9;
        }
    }
    else if (matchValue[0].tag === 4) {
        if (matchValue[1] === 0) {
            pattern_matching_result = 3;
            value = matchValue[0].fields[0].value;
        }
        else if (matchValue[1] === 1) {
            pattern_matching_result = 4;
            input = matchValue[0].fields[0].i;
        }
        else {
            pattern_matching_result = 9;
        }
    }
    else if (matchValue[0].tag === 2) {
        pattern_matching_result = 5;
        node = matchValue[0].fields[0].analyser;
    }
    else if (matchValue[0].tag === 1) {
        pattern_matching_result = 6;
        rate = matchValue[0].fields[0].rate;
    }
    else if (matchValue[0].tag === 5) {
        if (matchValue[1] === 0) {
            pattern_matching_result = 7;
            freq_1 = matchValue[0].fields[0].freq;
        }
        else if (matchValue[1] === 1) {
            pattern_matching_result = 8;
            input_1 = matchValue[0].fields[0].i;
        }
        else {
            pattern_matching_result = 9;
        }
    }
    else {
        pattern_matching_result = 0;
        merger_1 = matchValue[0].fields[0].merger;
    }
    switch (pattern_matching_result) {
        case 0: {
            return bind((clo1 = (merger = merger_1, (_arg1) => {
                const label = _arg1.label;
                switch (label) {
                    case "l out": {
                        return new DestConnectArgs(0, [merger, 0]);
                    }
                    case "r out": {
                        return new DestConnectArgs(0, [merger, 1]);
                    }
                    default: {
                        return void 0;
                    }
                }
            }), (arg10) => clo1(arg10)), getModJack(jackNumber, md));
        }
        case 1: {
            return new DestConnectArgs(1, freq);
        }
        case 2: {
            return new DestConnectArgs(1, detune);
        }
        case 3: {
            return new DestConnectArgs(1, value);
        }
        case 4: {
            return new DestConnectArgs(0, [input, void 0]);
        }
        case 5: {
            return new DestConnectArgs(0, [node, void 0]);
        }
        case 6: {
            return new DestConnectArgs(1, rate);
        }
        case 7: {
            return new DestConnectArgs(0, [freq_1, void 0]);
        }
        case 8: {
            return new DestConnectArgs(0, [input_1, void 0]);
        }
        case 9: {
            return void 0;
        }
    }
}

function changeConnection(disconnect_1, _arg1, mods) {
    const cable = _arg1;
    void map2(uncurry(2, disconnect_1 ? ((tupledArg) => disconnectNodes(tupledArg[0], tupledArg[1])) : ((tupledArg_1) => connectNodes(tupledArg_1[0], tupledArg_1[1]))), bind((md) => toAudioSource(cable.src[1], md), tryFind(cable.src[0], mods)), bind((md_1) => toAudioDest(cable.dest[1], md_1), tryFind(cable.dest[0], mods)));
    return cable;
}

export const connect = (arg10$0040) => ((mods) => changeConnection(false, arg10$0040, mods));

export const disconnect = (arg10$0040) => ((mods) => changeConnection(true, arg10$0040, mods));

export function sampleRate(context) {
    return (context.sampleRate * 1) / 1;
}

export function control(_arg1) {
    const _e = _arg1;
    const value = _e.value;
    return (table) => change_1(_e.position, (option) => map_2((_arg2) => {
        let matchValue;
        const m = _arg2;
        return new Mod(m.def, (matchValue = [m.state, _e.name], (matchValue[0].tag === 1) ? ((matchValue[1] === "rate") ? (matchValue[0].fields[0].rate.value = value, matchValue[0]) : ((matchValue[1] === "offset") ? (matchValue[0].fields[0].dcOffset.value = value, matchValue[0]) : matchValue[0])) : ((matchValue[0].tag === 0) ? ((matchValue[1] === "freq") ? (matchValue[0].fields[0].freq.value = value, matchValue[0]) : ((matchValue[1] === "detune") ? (matchValue[0].fields[0].detune.value = value, matchValue[0]) : matchValue[0])) : ((matchValue[0].tag === 2) ? ((matchValue[1] === "duration") ? (new ModState(2, new ScopeState(matchValue[0].fields[0].analyser, value * 1, matchValue[0].fields[0].offset, matchValue[0].fields[0].range, matchValue[0].fields[0].canvas, matchValue[0].fields[0].timeDomainData))) : ((matchValue[1] === "range") ? (new ModState(2, new ScopeState(matchValue[0].fields[0].analyser, matchValue[0].fields[0].frameDuration, matchValue[0].fields[0].offset, value * 1, matchValue[0].fields[0].canvas, matchValue[0].fields[0].timeDomainData))) : matchValue[0])) : ((matchValue[0].tag === 4) ? (matchValue[0].fields[0].o.gain.value = value, matchValue[0]) : ((matchValue[0].tag === 5) ? ((matchValue[1] === "freq") ? (matchValue[0].fields[0].o.frequency.value = value, matchValue[0]) : ((matchValue[1] === "q") ? (matchValue[0].fields[0].o.Q.value = value, matchValue[0]) : ((matchValue[1] === "gain") ? (matchValue[0].fields[0].o.gain.value = value, matchValue[0]) : matchValue[0]))) : matchValue[0]))))));
    }, option), table);
}

export function mapScope(change, _arg1) {
    const m = _arg1;
    const state = m.state;
    return new Mod(m.def, (state.tag === 2) ? (new ModState(2, change(state.fields[0]))) : state);
}

export function getFloatValue(label, state) {
    const matchValue = [state, label];
    let pattern_matching_result, state_1, state_2, state_3, state_4, state_5, state_6, state_7, state_8, state_9, state_10;
    if (matchValue[0].tag === 2) {
        if (matchValue[1] === "duration") {
            pattern_matching_result = 0;
            state_1 = matchValue[0].fields[0];
        }
        else if (matchValue[1] === "range") {
            pattern_matching_result = 1;
            state_2 = matchValue[0].fields[0];
        }
        else {
            pattern_matching_result = 10;
        }
    }
    else if (matchValue[0].tag === 1) {
        if (matchValue[1] === "rate") {
            pattern_matching_result = 2;
            state_3 = matchValue[0].fields[0];
        }
        else if (matchValue[1] === "offset") {
            pattern_matching_result = 3;
            state_4 = matchValue[0].fields[0];
        }
        else {
            pattern_matching_result = 10;
        }
    }
    else if (matchValue[0].tag === 0) {
        if (matchValue[1] === "freq") {
            pattern_matching_result = 4;
            state_5 = matchValue[0].fields[0];
        }
        else if (matchValue[1] === "detune") {
            pattern_matching_result = 5;
            state_6 = matchValue[0].fields[0];
        }
        else {
            pattern_matching_result = 10;
        }
    }
    else if (matchValue[0].tag === 4) {
        if (matchValue[1] === "gain") {
            pattern_matching_result = 6;
            state_7 = matchValue[0].fields[0];
        }
        else {
            pattern_matching_result = 10;
        }
    }
    else if (matchValue[0].tag === 5) {
        if (matchValue[1] === "freq") {
            pattern_matching_result = 7;
            state_8 = matchValue[0].fields[0];
        }
        else if (matchValue[1] === "q") {
            pattern_matching_result = 8;
            state_9 = matchValue[0].fields[0];
        }
        else if (matchValue[1] === "gain") {
            pattern_matching_result = 9;
            state_10 = matchValue[0].fields[0];
        }
        else {
            pattern_matching_result = 10;
        }
    }
    else {
        pattern_matching_result = 10;
    }
    switch (pattern_matching_result) {
        case 0: {
            return state_1.frameDuration;
        }
        case 1: {
            return state_2.range;
        }
        case 2: {
            return state_3.rate.value;
        }
        case 3: {
            return state_4.dcOffset.value;
        }
        case 4: {
            return state_5.freq.value;
        }
        case 5: {
            return state_6.detune.value;
        }
        case 6: {
            return state_7.o.gain.value;
        }
        case 7: {
            return state_8.o.frequency.value;
        }
        case 8: {
            return state_9.o.Q.value;
        }
        case 9: {
            return state_10.o.gain.value;
        }
        case 10: {
            return 0;
        }
    }
}

export function bytesToVolts(bytes) {
    let pattern_matching_result;
    if ((!equalsWith((x, y) => comparePrimitives(x, y), bytes, null)) ? (bytes.length === 3) : false) {
        if (bytes[0] === 144) {
            pattern_matching_result = 0;
        }
        else {
            pattern_matching_result = 1;
        }
    }
    else {
        pattern_matching_result = 1;
    }
    switch (pattern_matching_result) {
        case 0: {
            const noteNumber = bytes[1];
            const _velocity = bytes[2];
            toConsole(printf("midi %A"))(noteNumber);
            return Math.pow(2, (noteNumber - 45) / 12);
        }
        case 1: {
            return void 0;
        }
    }
}

export function midi(bytes) {
    return (table) => map_3((_position, _arg1) => {
        let matchValue, volts;
        const m = _arg1;
        const state = m.state;
        return new Mod(m.def, (state.tag === 3) ? (matchValue = bytesToVolts(bytes), (matchValue == null) ? state : (volts = matchValue, (toConsole(printf("volts %A"))(volts), (state.fields[0].midiPitch.offset.value = volts, state)))) : state);
    }, table);
}

